import type { JobReasonCodeInput } from '@chukua/common';
import { Button, Table } from 'antd';
import { MessageSquareWarning, Plus } from 'lucide-react';
import { useState } from 'react';
import { EmptyState } from '../../../../../shared/components/EmptyState';
import { TableSkeleton } from '../../../../../shared/components/TableSkeleton';
import { buildReasonCodeColumns } from './build-reason-code-columns';
import type { JobReasonCode } from '../../../reference-data.api';
import { ReasonCodeFormDrawer } from './ReasonCodeFormDrawer';

export interface ReasonCodesPanelProps {
  codes: JobReasonCode[];
  loading: boolean;
  saveError: string | null;
  saving: boolean;
  onCreate: (input: JobReasonCodeInput) => Promise<boolean>;
  onUpdate: (id: string, input: JobReasonCodeInput) => Promise<boolean>;
  onDeactivate: (row: JobReasonCode) => Promise<void>;
  onReactivate: (row: JobReasonCode) => Promise<void>;
  onResetSaveError: () => void;
}

/** Why a job can be held or cancelled (gitaumoses4/chukua#40) — alongside the free-text reason on the action itself. */
export function ReasonCodesPanel({
  codes,
  loading,
  saveError,
  saving,
  onCreate,
  onUpdate,
  onDeactivate,
  onReactivate,
  onResetSaveError,
}: ReasonCodesPanelProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<JobReasonCode | undefined>(undefined);

  function openCreate() {
    onResetSaveError();
    setEditing(undefined);
    setDrawerOpen(true);
  }

  function openEdit(row: JobReasonCode) {
    onResetSaveError();
    setEditing(row);
    setDrawerOpen(true);
  }

  const columns = buildReasonCodeColumns({ onEdit: openEdit, onDeactivate, onReactivate });
  const newButton = (
    <Button size="small" icon={<Plus className="size-4" />} onClick={openCreate}>
      New reason code
    </Button>
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-fg">Reason codes</h3>
        {newButton}
      </div>
      {loading && <TableSkeleton label="Loading reason codes" />}
      {!loading && codes.length === 0 && (
        <EmptyState
          icon={MessageSquareWarning}
          title="No reason codes yet"
          description="This catalogue ships empty — add the reasons a job can be held or cancelled for."
        >
          {newButton}
        </EmptyState>
      )}
      {!loading && codes.length > 0 && (
        <Table rowKey="id" size="small" dataSource={codes} columns={columns} pagination={{ pageSize: 10 }} scroll={{ x: 'max-content' }} />
      )}
      <ReasonCodeFormDrawer
        open={drawerOpen}
        editing={editing}
        error={saveError}
        submitting={saving}
        onSubmit={(input) => (editing ? onUpdate(editing.id, input) : onCreate(input))}
        onClose={() => setDrawerOpen(false)}
      />
    </div>
  );
}
